
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Course, User, BirdieGameGroup, CurrentRound } from "@/types";
import { getGameWithPlayers } from "@/integrations/supabase/game";
import { getCourseById } from "@/integrations/supabase/courseService";
import { toast } from "@/hooks/use-toast";

export const useGameData = (gameId?: string) => {
  const navigate = useNavigate();
  const [selectedGolfers, setSelectedGolfers] = useState<User[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [activeRound, setActiveRound] = useState<CurrentRound | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load game data if gameId is provided
  useEffect(() => {
    if (gameId) {
      loadGameData(gameId);
    } else {
      // Check localStorage for any active rounds on component mount
      const storedRound = localStorage.getItem('activeRound');
      if (storedRound) {
        try {
          const parsedRound = JSON.parse(storedRound) as CurrentRound;
          if (parsedRound.golfers && parsedRound.courseId && parsedRound.date) {
            const roundDate = new Date(parsedRound.date).toDateString();
            const today = new Date().toDateString();
            if (roundDate !== today) {
              localStorage.removeItem('activeRound');
            } else {
              setIsLoading(true);
              setActiveRound(parsedRound);
              setSelectedGolfers(parsedRound.golfers);

              // Fetch course from Supabase
              getCourseById(parsedRound.courseId).then(course => {
                if (course) {
                  setSelectedCourse(course);
                }
              }).finally(() => {
                setIsLoading(false);
              });
            }
          }
        } catch (e) {
          console.error("Error parsing stored round:", e);
          localStorage.removeItem('activeRound');
        }
      }
    }
  }, [gameId]);

  const loadGameData = async (gameId: string) => {
    try {
      setIsLoading(true);
      
      // Get game data with players
      const gameData = await getGameWithPlayers(gameId);
      
      if (!gameData) {
        toast({
          title: "Error",
          description: "Could not load game data. Game not found or you don't have access.",
          variant: "destructive",
        });
        navigate("/games");
        return;
      }
      
      // Get course data
      const courseData = await getCourseById(gameData.course_id);
      
      if (!courseData) {
        toast({
          title: "Error",
          description: "Could not load course data for this game.",
          variant: "destructive",
        });
        navigate("/games");
        return;
      }
      
      // Format players as User objects
      const players: User[] = gameData.players.map((player) => ({
        id: player.id,
        name: player.name || "Unknown Player",
        email: player.email,
        imageUrl: player.image_url
      }));
      
      // Set game data
      setSelectedGolfers(players);
      setSelectedCourse(courseData);
      
      // Create eligibleGames array with this game
      const eligibleGame: BirdieGameGroup = {
        id: gameData.id,
        name: gameData.name,
        courseId: gameData.course_id,
        courseName: courseData.location ? `${courseData.name}, ${courseData.location}` : courseData.name,
        year: new Date(gameData.start_date).getUTCFullYear(),
        startDate: gameData.start_date,
        endDate: gameData.end_date,
        durationType: gameData.duration_type ?? 'open_ended',
        members: players,
        bet: gameData.bet,
        winner: undefined,
        isPaid: false,
        isArchived: gameData.end_date ? new Date(gameData.end_date) < new Date() : false,
        recurringGroupId: gameData.recurring_group_id ?? null,
        isRecurring: gameData.is_recurring ?? false,
        isLoneWolf: gameData.is_lone_wolf ?? false,
        isSample: gameData.is_sample ?? false,
      };
      
      // Set active round data
      const roundData: CurrentRound = {
        golfers: players,
        courseId: courseData.id,
        date: new Date().toISOString(),
        eligibleGames: [eligibleGame],
      };
      
      setActiveRound(roundData);
      localStorage.setItem('activeRound', JSON.stringify(roundData));
    } catch (error) {
      console.error("Error loading game data:", error);
      toast({
        title: "Error",
        description: "An error occurred while loading the game.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    selectedGolfers,
    setSelectedGolfers,
    selectedCourse,
    setSelectedCourse,
    activeRound,
    setActiveRound,
    isLoading
  };
};
